import {
	Box,
	Divider,
	Flex,
	Input,
	Stack,
	UnstyledButton,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { showNotification } from '@mantine/notifications';
import { Button, IconButton, Text, TextInput } from '@repo/foundations';
import axios from 'axios';
import { map, startCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import type { Ref } from 'react';
import { useCallback, useRef, useState } from 'react';
import { api } from '../../../../../network';
import { useAuthUser, type IUser, type IWorkspace } from '../../../../api';
import { isImpersonating } from '../../../../utils/cache/utils';
import { useFeatureFlags } from '../../../../utils/featureFlags';
import { isSecodaCoEmail } from '../../../../utils/utils';
import { openModal } from '../../../ModalManager';
import { UserAvatar } from '../../../UserAvatar';
import { workspaceSwitchStore } from './store';
import { WorkspaceItem } from './WorkspaceItem';
import { useStyles } from './WorkspaceSelectorSubMenu.styles';

function TotpModalContent({ user }: { user: IUser }) {
	const input = useRef<HTMLInputElement>();
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = useCallback(async () => {
		if (input?.current?.value) {
			const errorResponse = await workspaceSwitchStore.impersonate(
				user.email,
				input?.current?.value
			);
			if (errorResponse) {
				setError(errorResponse);
			}
		}
	}, [user.email]);

	return (
		<>
			<TextInput
				ref={input as Ref<HTMLInputElement>}
				label="Confirmation code"
				placeholder="Enter confirmation code"
				data-autofocus
			/>
			{error && <Text>{error}</Text>}
			<Button w="100%" onClick={handleSubmit} mt="md" variant="primary">
				Submit
			</Button>
		</>
	);
}

interface WorkspaceSelectorSubMenuProps {
	workspaces: (IWorkspace & { is_active: boolean })[];
}

export const WorkspaceSelectorSubMenu = observer(
	({ workspaces }: WorkspaceSelectorSubMenuProps) => {
		const { classes } = useStyles();
		const [opened, handlers] = useDisclosure();
		const { createWorkspace } = useFeatureFlags();
		const { user, isAdminUser } = useAuthUser();

		const openModalManager = useCallback((impersonatedUser: IUser) => {
			openModal({
				title: `Impersonating ${impersonatedUser.email}`,
				children: <TotpModalContent user={impersonatedUser} />,
			});
		}, []);

		const handleCreateWorkspace = useCallback(() => {
			handlers.open();
			workspaceSwitchStore.createWorkspace();
		}, [handlers]);

		const handleAddAuthorizedDomain = useCallback(async () => {
			const domain = workspaceSwitchStore.authorizedDomainSearchTerm;
			try {
				if (
					domain.match(
						'^(?!-)[A-Za-z0-9-*]+([\\-\\.]{1}[a-z0-9]+)*\\.[A-Za-z]{2,6}$'
					)
				) {
					await axios.post(`${api()}auth/utilities/authorized_domain`, {
						domain,
					});
					showNotification({
						title: 'Success',
						message: `Added ${domain} to authorized domains`,
						color: 'green',
					});
				} else {
					showNotification({
						title: 'Error',
						message: `Invalid format for domain: ${domain}`,
						color: 'red',
					});
				}
			} catch (error) {
				showNotification({
					title: 'Error',
					message: `Error adding domain: ${domain} ${error}`,
					color: 'red',
				});
			}
		}, []);

		return (
			<>
				<Stack className={classes.workspacesListSectionWrapper}>
					<Box className={classes.itemWrapper}>
						<Text size="xs" weight="semibold" color="text/secondary/default">
							{user?.email}
						</Text>
					</Box>
					{map(workspaces, (workspace) => (
						<WorkspaceItem key={workspace.id} workspace={workspace} />
					))}
				</Stack>
				{(isAdminUser || workspaceSwitchStore.account?.is_superuser) &&
					createWorkspace && (
						<>
							<Divider />
							<Stack className={classes.sectionWrapper}>
								<UnstyledButton
									disabled={opened}
									className={classes.menuRowButton}
									onClick={handleCreateWorkspace}
								>
									<Text size="xs">Create a new workspace</Text>
								</UnstyledButton>
							</Stack>
						</>
					)}
				{isSecodaCoEmail(user?.email) && (
					<>
						<Divider />
						<Stack className={classes.sectionWrapper}>
							<Stack spacing="xs" py="xs">
								<Box className={classes.itemWrapper}>
									<Text
										size="xs"
										weight="semibold"
										color="text/secondary/default"
									>
										Authorized instance domains
									</Text>
								</Box>
								<Flex>
									<Input
										size="xs"
										px="xs"
										placeholder="Add a domain"
										value={workspaceSwitchStore.authorizedDomainSearchTerm}
										onChange={(e: any) => {
											workspaceSwitchStore.authorizedDomainSearchTerm =
												e.target.value;
										}}
									/>
									<IconButton
										iconName="plus"
										onClick={handleAddAuthorizedDomain}
									/>
								</Flex>
							</Stack>
						</Stack>
					</>
				)}
				{workspaceSwitchStore.account?.is_superuser && !isImpersonating() && (
					<Stack className={classes.sectionWrapper}>
						<Divider />
						<Stack spacing="xs" py="xs">
							<Box className={classes.itemWrapper}>
								<Text
									size="xs"
									weight="semibold"
									color="text/secondary/default"
								>
									Impersonate
								</Text>
							</Box>
							<Input
								size="xs"
								px="xs"
								placeholder="Search for a user"
								value={workspaceSwitchStore.usersSearchTerm}
								onChange={(e: any) => {
									workspaceSwitchStore.usersSearchTerm = e.target.value;
									workspaceSwitchStore.searchUsers();
								}}
							/>
						</Stack>
						<Box className={classes.userList}>
							{workspaceSwitchStore.filteredUsers.map((filteredUser) => (
								<Flex
									key={filteredUser.id}
									className={classes.workspace}
									onClick={() => openModalManager(filteredUser)}
									gap={8}
									align="center"
								>
									<UserAvatar user={filteredUser} size="xxs" />
									<Stack spacing={0} p={0}>
										<Text size="sm" weight="semibold">
											{filteredUser.email}
										</Text>
										<Text size="xs">{startCase(filteredUser.role)}</Text>
									</Stack>
								</Flex>
							))}
						</Box>
					</Stack>
				)}
			</>
		);
	}
);
